// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Post-login onboarding  (/welcome/profile → /welcome/consent → /welcome/aboard)
// Step 5 of 5 — optional WhatsApp-message preferences (Terms/Privacy/Disclaimer are accepted on the
// earlier charter step, /welcome/terms).
// Choices are stored on this device only (utils/consent.ts); no server-side consent log yet.
// Next: /welcome/aboard after a login, otherwise the store.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, BellRing, Check, Lock, MessageSquareText, ShieldCheck, Trash2, Truck } from "lucide-react";
import PageTransition from "../components/PageTransition";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { chooseGuest } from "../utils/entryGate";
import { readConsent, saveConsent } from "../utils/consent";

const PROMISES = [
  {
    icon: Lock,
    title: "Encrypted in transit",
    body: "Everything between your device and ZenFresh travels over HTTPS.",
  },
  {
    icon: ShieldCheck,
    title: "Login codes only, unless you opt in",
    body: "Without your say-so, the only message you get from us is your sign-in code by SMS.",
  },
  {
    icon: Truck,
    title: "Used to deliver your order",
    body: "Your number and address are used to fulfil and support your order, nothing else.",
  },
  {
    icon: Trash2,
    title: "Your choices, changeable",
    body: "Come back to this page any time to change what you agreed to below.",
  },
];

interface SwitchRowProps {
  id: string;
  icon: typeof BellRing;
  title: string;
  tag: string;
  body: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  accent?: boolean;
}

function SwitchRow({ id, icon: Icon, title, tag, body, checked, onChange, accent }: SwitchRowProps) {
  return (
    <div className="flex items-start gap-4 rounded-2xl bg-ocean-700/5 p-5 dark:bg-white/10">
      <span
        aria-hidden="true"
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${
          accent ? "bg-whatsapp text-ocean-950" : "bg-teal-100 text-teal-700"
        }`}
      >
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h3 id={`${id}-label`} className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">
            {title}
          </h3>
          <span className="rounded-full bg-ocean-700/10 px-2.5 py-0.5 text-[11px] font-bold text-ocean-800 dark:bg-white/15 dark:text-foam-100">
            {tag}
          </span>
        </div>
        <p id={`${id}-desc`} className="mt-1 text-sm leading-relaxed text-ocean-800/85 dark:text-foam-100/80">
          {body}
        </p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={`${id}-label`}
        aria-describedby={`${id}-desc`}
        onClick={() => onChange(!checked)}
        className={`relative mt-1 h-8 w-14 shrink-0 rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 ${
          checked ? "bg-teal-700" : "bg-ocean-900/25 dark:bg-white/25"
        }`}
      >
        <span
          aria-hidden="true"
          className={`absolute left-1 top-1 h-6 w-6 rounded-full bg-white shadow transition-transform motion-reduce:transition-none ${
            checked ? "translate-x-6" : ""
          }`}
        />
      </button>
    </div>
  );
}

function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-[1.75rem] bg-white p-6 shadow-xl shadow-ocean-900/10 dark:bg-ocean-900 sm:p-8 ${className}`}>
      {children}
    </div>
  );
}

const PRIMARY_BUTTON =
  "inline-flex min-h-14 items-center justify-center gap-2 rounded-full px-8 text-lg font-bold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600";

export default function Consent() {
  useDocumentTitle("Privacy & consent");
  const navigate = useNavigate();
  const fromLogin = (useLocation().state as { fromLogin?: boolean } | null)?.fromLogin === true;
  const previous = readConsent();
  const [orderUpdates, setOrderUpdates] = useState(previous?.orderUpdates ?? true);
  const [seasonalDrops, setSeasonalDrops] = useState(previous?.seasonalDrops ?? false);
  const allOn = orderUpdates && seasonalDrops;

  const finish = (choices: { orderUpdates: boolean; seasonalDrops: boolean }) => {
    saveConsent(choices);
    chooseGuest(); // whoever has made this choice has passed the entry gate
    // Straight after a mobile sign-in, the welcome screen comes next.
    if (fromLogin) navigate("/welcome/aboard", { state: { fromLogin: true } });
    else navigate("/");
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-foam-50 pt-8 text-ocean-950 dark:bg-ocean-950 dark:text-foam-50">
        <header className="border-b border-ocean-900/5 bg-white dark:border-white/10 dark:bg-ocean-900">
          <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-4 px-4 py-4 sm:px-8 lg:min-h-[100px]">
            <Link
              to="/welcome"
              className="flex items-center gap-2 rounded-lg text-sm font-semibold hover:text-teal-700 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-teal-600"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Back to sign in
            </Link>
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="" className="h-11 w-11 object-contain" />
              <p className="font-display text-xl font-bold text-teal-700 dark:text-teal-300">ZenFresh</p>
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-[1500px] px-4 pb-10 pt-8 sm:px-8">
          <div className="flex items-center gap-4">
            <span
              aria-hidden="true"
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-teal-700 text-xl font-bold text-white"
            >
              3
            </span>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-teal-700 dark:text-teal-300">
                Step 5 of 5
              </p>
              <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Message preferences</h1>
            </div>
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-[5fr_7fr]">
            <div className="flex flex-col gap-6">
              <Card className="bg-gradient-to-br from-teal-100/70 via-white to-white dark:from-ocean-900 dark:via-ocean-900">
                <p className="text-[11px] font-bold uppercase tracking-widest text-ocean-800/70 dark:text-foam-100/60">
                  How we treat your data
                </p>
                <h2 className="mt-2 font-display text-2xl font-bold">Fresh from the coast, handled with care</h2>
                <p className="mt-3 text-base leading-relaxed text-ocean-800/85 dark:text-foam-100/80">
                  We collect only what an order needs: your mobile number to sign you in, and your delivery details to reach you.
                </p>
                <ul className="mt-5 flex flex-col gap-3">
                  {PROMISES.map(({ icon: Icon, title, body }) => (
                    <li key={title} className="flex gap-4 rounded-2xl bg-ocean-700/5 p-4 dark:bg-white/10">
                      <Icon className="mt-0.5 h-6 w-6 shrink-0 text-teal-700 dark:text-teal-300" aria-hidden="true" />
                      <div>
                        <p className="text-sm font-bold">{title}</p>
                        <p className="text-sm text-ocean-800/85 dark:text-foam-100/80">{body}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>

            <Card>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-display text-2xl font-bold">Choose your messages</h2>
                  <p className="mt-1 text-sm text-ocean-800/85 dark:text-foam-100/80">
                    Choose how ZenFresh may contact you. You can change these later.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setOrderUpdates(true);
                    setSeasonalDrops(true);
                  }}
                  aria-disabled={allOn}
                  className="inline-flex items-center gap-2 rounded-full bg-teal-100 px-4 py-2 text-sm font-bold text-teal-700 hover:bg-teal-200 aria-disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
                >
                  <Check className="h-4 w-4" aria-hidden="true" />
                  {allOn ? "All options on" : "Turn on all options"}
                </button>
              </div>

              <div className="mt-6 flex flex-col gap-4">
                <SwitchRow
                  id="order-updates"
                  icon={MessageSquareText}
                  accent
                  title="Order updates on WhatsApp"
                  tag="Recommended"
                  body="Delivery status and cold-chain check-ins for your orders, sent to your WhatsApp number."
                  checked={orderUpdates}
                  onChange={setOrderUpdates}
                />
                <SwitchRow
                  id="seasonal-drops"
                  icon={BellRing}
                  title="Seasonal catch alerts"
                  tag="Optional"
                  body="A heads-up when a seasonal catch lands, such as the first pomfret or lobster of the season."
                  checked={seasonalDrops}
                  onChange={setSeasonalDrops}
                />
              </div>

              <p className="mt-5 rounded-2xl bg-teal-100/60 p-4 text-sm text-ocean-900 dark:bg-teal-700/20 dark:text-foam-100">
                Your choices are saved on this device. Reopen /welcome/consent any time to change them.
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => finish({ orderUpdates, seasonalDrops })}
                  className={`${PRIMARY_BUTTON} flex-1 bg-teal-700 text-white hover:bg-teal-600`}
                >
                  Confirm &amp; enter the store
                  <ArrowRight className="h-5 w-5" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => finish({ orderUpdates: false, seasonalDrops: false })}
                  className={`${PRIMARY_BUTTON} bg-ocean-700/10 text-base text-ocean-950 hover:bg-ocean-700/15 dark:bg-white/10 dark:text-foam-50`}
                >
                  Accept essentials only
                </button>
              </div>
              <p className="mt-4 text-center text-xs text-ocean-800/80 dark:text-foam-100/70">
                Optional messages are never a condition of ordering. You accepted the Terms, Privacy Policy and Disclaimer earlier.
              </p>
            </Card>
          </div>
        </main>

        <footer className="border-t border-ocean-900/5 bg-white dark:border-white/10 dark:bg-ocean-900">
          <div className="mx-auto flex max-w-[1500px] flex-wrap items-center justify-between gap-3 px-4 py-5 text-sm sm:px-8">
            <p className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-ocean-800 dark:text-foam-100" aria-hidden="true" />
              Encrypted OTP login
            </p>
            <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <Link to="/privacy" className="hover:underline">Privacy Policy</Link>
              <span aria-hidden="true">•</span>
              <Link to="/terms" className="hover:underline">Terms of Service</Link>
              <span aria-hidden="true">•</span>
              <span>© {new Date().getFullYear()} ZenFresh</span>
            </p>
          </div>
        </footer>
      </div>
    </PageTransition>
  );
}
